import TopSortCardsBadge from '@/src/components/common/srp/topSortCardsLabel/topSortCardsLabel'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useGetSortedTopCards } from '@/src/hooks/srp/getGetSortedTopCards'
import { useRTSolutionCardStatesManager } from '@/src/hooks/srp/RTSolutionView/RTSolutionCardStatesManager'
import { FlightCard } from '@/src/types/flight'
import type { ProcessedFlightsIndex } from '@/src/utils/srp/aiRecommendation/ranking/types'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Card, Collapse } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useCallback } from 'react'
import ReactList from 'react-list'
import RtSolutionCard from './rtSolutionCard'

interface RtSolutionCardWrapperProps {
    samePriceCards: FlightCard[]
    onSelectCard?: (card: FlightCard) => void
    onViewDetailsClick?: (card: FlightCard[], showBaggage?: boolean) => void
    smartSortIdMetrics?: ProcessedFlightsIndex
}

const RtSolutionCardWrapper: FC<RtSolutionCardWrapperProps> = ({
    samePriceCards,
    onSelectCard,
    onViewDetailsClick,
    smartSortIdMetrics
}) => {
    const { expandSamePriceCard, handleExpandSamePriceCard, mainCard, extraCards } =
        useRTSolutionCardStatesManager(samePriceCards)
    const tFlightDetails = useTranslations('srpFlightDetails')
    const topSortedCards = useGetSortedTopCards()

    const sortedTopCard = topSortedCards.find(sortOption => sortOption?.card?.id === mainCard.id)
    const renderSamePriceFlightCard = useCallback(
        (index: number) => {
            const flight = extraCards[index]

            const sortedExtraTopCard = topSortedCards.find(
                sortOption => sortOption?.card?.id === flight.id
            )

            if (sortedExtraTopCard) {
                return (
                    <TopSortCardsBadge
                        key={flight.id}
                        label={sortedExtraTopCard.label}
                        isActive={!!sortedExtraTopCard.isActive}
                        sortOptionId={sortedExtraTopCard.id}
                        isMobile={false}
                        isCombined={sortedExtraTopCard.isCombined}
                    >
                        <Box
                            sx={{
                                mx: 1.5,
                                my: 1.5,
                                p: 1.5,
                                bgcolor: 'white',
                                borderRadius: 1
                            }}
                        >
                            <ErrorBoundaryWrapper
                                identifier="RtSolutionExpandSamePriceCard-desktop-srp"
                                fallback={null}
                            >
                                <RtSolutionCard
                                    card={flight}
                                    onSelectCard={onSelectCard}
                                    onViewDetailsClick={onViewDetailsClick}
                                    smartMetrics={smartSortIdMetrics?.[flight.id]}
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    </TopSortCardsBadge>
                )
            }

            return (
                <Box
                    key={flight.id}
                    sx={{
                        mx: 1.5,
                        my: 1.5,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1
                    }}
                >
                    <ErrorBoundaryWrapper identifier="RtSolutionExpandSamePriceCard-desktop-srp" fallback={null}>
                        <RtSolutionCard
                            card={flight}
                            onSelectCard={onSelectCard}
                            onViewDetailsClick={onViewDetailsClick}
                            smartMetrics={smartSortIdMetrics?.[flight.id]}
                        />
                    </ErrorBoundaryWrapper>
                </Box>
            )
        },
        [extraCards, onSelectCard, onViewDetailsClick, topSortedCards]
    )


    if (sortedTopCard) {
        return (
            <TopSortCardsBadge
                label={sortedTopCard.label}
                isActive={!!sortedTopCard.isActive}
                sortOptionId={sortedTopCard.id}
                isMobile={false}
                isCombined={sortedTopCard.isCombined}
            >
                <Card
                    sx={{
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden',
                        boxShadow: 'none',
                        p: 0,
                        mx: 0,
                        position: 'relative'
                    }}
                >
                    <Box height={"10px"}></Box>
                    <YellowBannerPromo promos={sortedTopCard?.card?.promos} />
                    <Box
                        sx={{
                            px: 3,
                            pt: (sortedTopCard?.card?.promos?.length || 0) > 0 ? 2.5 : 1.5,
                            pb: 1.5
                        }}
                    >
                        <ErrorBoundaryWrapper
                            identifier={`RtSolutionMainCard-desktop-srp-${sortedTopCard?.card?.id}`}
                            fallback={null}
                        >
                            <RtSolutionCard
                                card={sortedTopCard?.card!}
                                onSelectCard={onSelectCard}
                                onViewDetailsClick={onViewDetailsClick}
                                smartMetrics={smartSortIdMetrics?.[sortedTopCard?.card?.id!]}
                            />
                        </ErrorBoundaryWrapper>
                    </Box>
                </Card>
            </TopSortCardsBadge>
        )
    }


    return (
        <Card
            sx={{
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                boxShadow: 'none',
                p: 0,
                mx: 0,
                position: 'relative'
            }}
        >
            <YellowBannerPromo promos={mainCard.promos} />
            <Box sx={{ px: 3, pt: mainCard.promos?.length > 0 ? 2.5 : 1.5, pb: 1.5 }}>
                <ErrorBoundaryWrapper identifier="RtSolutionMainCard-desktop-srp" fallback={null}>
                    <RtSolutionCard
                        card={mainCard}
                        onSelectCard={onSelectCard}
                        onViewDetailsClick={onViewDetailsClick}
                        smartMetrics={smartSortIdMetrics?.[mainCard.id]}
                    />
                </ErrorBoundaryWrapper>
            </Box>
            {/* More fare options bar */}
            {extraCards.length > 0 && !expandSamePriceCard && (
                <Box
                    onClick={handleExpandSamePriceCard}
                    sx={{
                        cursor: 'pointer',
                        px: 3,
                        background: '#f7f7f7',
                        borderTop: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        fontWeight: 500,
                        color: 'primary.main',
                        fontSize: '0.90rem',
                        height: 35,
                        '&:hover': { background: '#f0f4ff' }
                    }}
                >
                    +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                    <KeyboardArrowDownIcon fontSize="small" />
                </Box>
            )}

            <Collapse in={expandSamePriceCard} timeout="auto" unmountOnExit>
                <Box sx={{ bgcolor: '#f5f8fa', pt: 1.5, pb: 1.5 }}>
                    <ReactList length={extraCards.length} itemRenderer={renderSamePriceFlightCard} />
                </Box>
                {/* Hide More bar */}
                <Box
                    onClick={handleExpandSamePriceCard}
                    sx={{
                        cursor: 'pointer',
                        px: 3,
                        background: '#f7f7f7',
                        borderTop: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        fontWeight: 500,
                        color: 'primary.main',
                        fontSize: '0.90rem',
                        height: 35,
                        '&:hover': { background: '#f0f4ff' }
                    }}
                >
                    {tFlightDetails('hideMoreOption')}
                </Box>
            </Collapse>
        </Card>
    )
}

export default RtSolutionCardWrapper
